/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState, useEffect, useCallback } from "react";
import "./App.scss";
import { LiveAPIProvider, useLiveAPIContext } from "./contexts/LiveAPIContext";
import { Dashboard } from "./components/dashboard/Dashboard";
import { Login } from "./components/login";
import ControlTray from "./components/control-tray/ControlTrayV2";
import SettingsDialog from "./components/settings-dialog/SettingsDialog";
import { GazeTracker } from "./components/gaze-tracker/GazeTracker";
import { WebcamOverlay } from "./components/gaze-tracker/WebcamOverlay";
import { PostureCalibration } from "./components/gaze-tracker/PostureCalibration";
import { VoiceGlow } from "./components/voice-glow/VoiceGlow";
import { SalesReport } from "./components/sales-report/SalesReport";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Landmark } from "@mediapipe/tasks-vision";
import { useTranscriptStore } from "./lib/store-transcript";
import { useReportStore } from "./lib/store-report";
import { analyzeConversation } from "./lib/report-analyzer";
import { analyzeQuests } from "./lib/quest-analyzer";
import { useSettingsStore } from "./lib/store-settings";
import { useAuthStore } from "./lib/store-auth";
import { ProfileSettings } from "./components/profile/ProfileSettings";
import { AdminLayout } from "./components/admin/AdminLayout";
import { useSimulationStore } from "./lib/store-simulations";
import { Simulation, Module, supabase } from "./lib/supabase";
import { ModulePlayer } from "./components/modules/ModulePlayer";
import { TalkingHeadAvatar } from "./components/talking-head/TalkingHeadAvatar";
// import { PastSessions } from "./components/dashboard/PastSessions";
import { LiveClientOptions } from "./types";
import { SalesCoachBot } from "./components/dashboard/SalesCoachBot";
import { ManagerLayout } from "./components/manager/ManagerLayout";


// API key backend'den alınacak - tarayıcıda gömülü değil
async function fetchApiKey(): Promise<string> {
  // Localhost'ta doğrudan env'den al (api endpoint yok)
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return process.env.REACT_APP_GEMINI_API_KEY || '';
  }

  try {
    const response = await fetch('/api/get-key');
    if (!response.ok) {
      throw new Error('API key alınamadı');
    }
    const { apiKey } = await response.json();
    return apiKey;
  } catch (error) {
    console.error('API key fetch error:', error);
    // Fallback: development için env'den al
    return process.env.REACT_APP_GEMINI_API_KEY || '';
  }
}


function StreamingConsole({ onBack, onGenerateReport }: { onBack: () => void; onGenerateReport: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const emotionBarRef = useRef<any>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mission tracker toggle (collapsed by default on mobile)
  const [missionsExpanded, setMissionsExpanded] = useState(!isMobile);
  const { avatarMode, disconnect, setConfig, connected } = useLiveAPIContext();
  const { activeSimulation } = useSimulationStore();

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = videoStream;
    }
  }, [videoStream]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const [showInstruction, setShowInstruction] = useState(!!activeSimulation?.config?.userInstruction);
  const [completedMissions, setCompletedMissions] = useState<Set<number>>(new Set());
  const [lastCompletedMission, setLastCompletedMission] = useState<number | null>(null);

  // Apply simulation config (Persona, Voice, Tools) atomically
  useEffect(() => {
    if (!activeSimulation?.config) return;

    console.log("🛠️ Preparing simulation session:", {
      title: activeSimulation.title,
      missions: activeSimulation.config.missions?.length || 0,
      persona: !!activeSimulation.config.systemInstruction
    });

    setConfig((prev) => {
      const next = { ...prev };

      // 1. System Instruction (Persona) + Quest Context
      if (activeSimulation.config.systemInstruction) {
        let basePersona = "";
        const si = activeSimulation.config.systemInstruction;

        if (typeof si === 'string') {
          basePersona = si;
        } else if (si.parts?.[0]?.text) {
          basePersona = si.parts[0].text;
        } else if (typeof si === 'object' && (si as any).text) {
          basePersona = (si as any).text;
        }

        const missionList = activeSimulation.config.missions?.length
          ? activeSimulation.config.missions.map((m: string, i: number) => `- Görev #${i}: ${m}`).join("\n")
          : "";

        const questContext = missionList
          ? `\n\n### ÖNEMLİ: GÖREV TAKİBİ\nŞu anki simülasyonda kullanıcının tamamlaması gereken özel görevler bulunmaktadır:\n${missionList}\n\nKullanıcı bu görevleri yerine getirdiğinde karakterin olarak tepki ver ve yapıcı bir şekilde geri dönüş yap. Görevlerin teknik takibi arka planda otomatik olarak yapılmaktadır, senin fonksiyon çağırmana gerek yoktur.`
          : "";

        next.systemInstruction = {
          parts: [{ text: basePersona + questContext }]
        };
      }

      // 2. Voice Config
      if (activeSimulation.config.voice) {
        next.speechConfig = {
          ...prev.speechConfig,
          voiceConfig: {
            ...prev.speechConfig?.voiceConfig,
            prebuiltVoiceConfig: {
              voiceName: activeSimulation.config.voice
            }
          }
        };
      }

      /* 
      // 3. Quest Tools - REMOVED (Migrated to transcript-based detection)
      if (activeSimulation.config.missions?.length) {
        ...
      }
      */

      console.log("✅ Simulation config ready (Background Quest Detection Enabled):", next);
      return next;
    });
  }, [activeSimulation, setConfig]);

  // Reset missions when disconnected or switching simulations
  useEffect(() => {
    if (!connected) {
      console.log("🔄 Resetting mission state (Disconnected)");
      setCompletedMissions(new Set());
      setLastCompletedMission(null);
    }
  }, [connected]);


  // Background Quest Detection using Transcripts
  const transcripts = useTranscriptStore((state) => state.transcripts);

  useEffect(() => {
    if (!connected || !activeSimulation?.config?.missions?.length) return;

    // Only analyze if there's a new transcript and not all missions are completed
    const missions = activeSimulation.config.missions;
    if (completedMissions.size === missions.length) return;

    // console.log("⏱️ Transcript changed, resetting analysis timer...");

    const timer = setTimeout(async () => {
      console.log("🔍 Background Quest Analysis STARTING (Recent transcript):");
      const newlyCompletedIndices = await analyzeQuests(transcripts, missions);

      if (newlyCompletedIndices.length > 0) {
        setCompletedMissions(prev => {
          let hasNew = false;
          const next = new Set(prev);

          newlyCompletedIndices.forEach(idx => {
            if (!next.has(idx)) {
              console.log("🎯 Mission", idx, "detected as fulfilled!");
              next.add(idx);
              setLastCompletedMission(idx);
              hasNew = true;
            }
          });

          if (hasNew) {
            setTimeout(() => setLastCompletedMission(null), 3000);
          }
          return next;
        });
      } else {
        console.log("✅ Analysis complete, no new missions detected.");
      }
    }, 1500); // 1.5s debounce for analysis

    return () => clearTimeout(timer);
  }, [transcripts, connected, activeSimulation, completedMissions.size]);

  const [gazeActive, /* setGazeActive */] = useState(false);
  const [showGazeCursor, setShowGazeCursor] = useState(false);

  const [landmarks, /* setLandmarks */] = useState<{ face: Landmark[][], pose: Landmark[][], hands: Landmark[][] }>({ face: [], pose: [], hands: [] });
  const [isCalibratingPosture, setIsCalibratingPosture] = useState(false);

  return (
    <div className="streaming-console">
      <div className={`console-content ${showInstruction ? 'blurred' : ''}`}>
        <header className="top-header">
          <div className="brand-logo">
            <img src="/logo.svg" alt="Saletone Logo" />
          </div>
        </header>
        <div className="main-app-area">
          <div className="video-grid">
            <div className="video-card ai-card">
              <TalkingHeadAvatar
                avatarUrl={
                  activeSimulation?.config?.avatarUrl ||
                  (avatarMode === "door2door" ? "/avatars/david.glb" : "/avatars/julia.glb")
                }
              />
            </div>

            {!isMobile && (
              <div className="video-card user-card">
                {videoStream && (
                  <>
                    <video
                      className="stream"
                      ref={videoRef}
                      autoPlay
                      playsInline
                    />
                    <WebcamOverlay landmarks={landmarks} videoRef={videoRef} />
                  </>
                )}
              </div>
            )}
          </div>

          {activeSimulation?.config?.missions?.length > 0 && (
            <>
              <button
                className="mission-toggle-btn"
                onClick={() => setMissionsExpanded(prev => !prev)}
              >
                <span className="mission-toggle-count">
                  {completedMissions.size}/{activeSimulation?.config?.missions?.length ?? 0}
                </span>
                <span className="material-symbols-outlined">
                  {missionsExpanded ? 'expand_less' : 'expand_more'}
                </span>
              </button>
              {missionsExpanded && (
                <div className="mission-tracker">
                  <div className="mission-label">Görevler</div>
                  <div className="mission-list">
                    {activeSimulation?.config?.missions?.map((m: string, i: number) => (
                      <div key={i} className={`mission-item ${completedMissions.has(i) ? 'completed' : ''} ${lastCompletedMission === i ? 'just-completed' : ''}`}>
                        <span className="material-symbols-outlined mi-icon">
                          {completedMissions.has(i) ? 'check_circle' : 'circle'}
                        </span>
                        <span className="mi-text">{m}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showInstruction && activeSimulation?.config?.userInstruction && (
        <div className="briefing-overlay">
          <div className="briefing-panel">
            <button className="briefing-close" onClick={onBack}>
              <span className="material-symbols-outlined">close</span>
            </button>
            <div className="briefing-top">
              <span className="briefing-tag">{activeSimulation?.title}</span>
              <h2>Görev Brifing</h2>
            </div>
            <div className="briefing-body">
              {activeSimulation.config.userInstruction}
            </div>
            <button className="briefing-start" onClick={() => setShowInstruction(false)}>
              Simülasyonu Başlat
            </button>
          </div>
        </div>
      )}
      <GazeTracker
        active={gazeActive}
        showCursor={showGazeCursor}
        onCalibrationComplete={() => setShowGazeCursor(true)}
      />

      {isCalibratingPosture && (
        <PostureCalibration
          landmarks={landmarks}
          videoStream={videoStream}
          onComplete={(baseline) => {
            emotionBarRef.current?.setPostureBaseline(baseline);
            setIsCalibratingPosture(false);
          }}
        />
      )}
      <ControlTray
        videoRef={videoRef}
        supportsVideo={!isMobile}
        onVideoStreamChange={setVideoStream}
        onGenerateReport={onGenerateReport}
      />
      <VoiceGlow />
    </div>
  );
}

// Personel Dashboard (mevcut dashboard)
function PersonelDashboard({ onStartSimulation, onOpenSettings, onOpenProfile, onLogout, onOpenPastSessions, showPastSessions, onBack, onViewReport, onOpenLeaderboard, showLeaderboard, onOpenModules, showModules, onStartModule }: {
  onStartSimulation: (sim: Simulation) => void;
  onOpenSettings: () => void;
  onOpenProfile: () => void;
  onLogout: () => void;
  onOpenPastSessions: () => void;
  showPastSessions?: boolean;
  onOpenLeaderboard: () => void;
  showLeaderboard?: boolean;
  onOpenModules: () => void;
  showModules?: boolean;
  onStartModule: (mod: Module) => void;
  onBack?: () => void;
  onViewReport?: () => void;
}) {
  return (
    <>
      <Dashboard
        onStartSimulation={onStartSimulation}
        onOpenSettings={onOpenSettings}
        onOpenProfile={onOpenProfile}
        onLogout={onLogout}
        onOpenPastSessions={onOpenPastSessions}
        showPastSessions={showPastSessions}
        onOpenLeaderboard={onOpenLeaderboard}
        showLeaderboard={showLeaderboard}
        onOpenModules={onOpenModules}
        showModules={showModules}
        onStartModule={onStartModule}
        onBack={onBack}
        onViewReport={onViewReport}
      />
      <SalesCoachBot />
    </>
  );
}

// Müdür Dashboard (placeholder - sonra geliştirilecek)
// Müdür Dashboard

function MudurDashboard({ onLogout, onStartSimulation }: { onLogout: () => void; onStartSimulation: (sim: Simulation) => void }) {
  return (
    <ManagerLayout
      onLogout={onLogout}
      onStartSimulation={onStartSimulation}
    />
  );
}

// Admin Dashboard
function AdminDashboard({ onLogout, onViewReport }: { onLogout: () => void; onViewReport: () => void }) {
  return <AdminLayout onLogout={onLogout} onViewReport={onViewReport} />;
}


function App() {
  const [view, setView] = useState<"login" | "dashboard" | "meeting" | "report" | "past-sessions" | "leaderboard" | "modules" | "module-player">("login");
  const [apiOptions, setApiOptions] = useState<LiveClientOptions | null>(null);
  const [isLoading, setLoading] = useState(false); // General loading
  const { checkSession, isAuthenticated, user, logout } = useAuthStore();
  const { setReport, setLoading: setReportLoading, setError: setReportError } = useReportStore();
  const { persona } = useSettingsStore();
  const { activeSimulation } = useSimulationStore();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [activeModule, setActiveModule] = useState<Module | null>(null);

  // API key'i backend'den al
  useEffect(() => {
    fetchApiKey().then(apiKey => {
      if (apiKey) {
        setApiOptions({ apiKey });
      }
    });
  }, []);

  // Oturum kontrolü
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Oturum durumuna göre view güncelle
  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated && user) {
        // Sadece login ekranındaysa dashboard'a yönlendir
        // Bu sayede meeting ekranındayken refresh olursa dashboard'a atmaz
        if (view === "login") {
          setView("dashboard");
        }
      } else {
        // Oturum yoksa her zaman login'e at
        setView("login");
      }
    }
  }, [isAuthenticated, isLoading, user, view]);

  const handleLoginSuccess = useCallback(() => {
    setView("dashboard");
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
    setView("login");
  }, [logout]);

  const handleStartSimulation = useCallback((simulation: Simulation) => {
    // Clear previous session data
    useTranscriptStore.getState().clearTranscripts();
    useReportStore.getState().closeReport();

    useSimulationStore.getState().setActiveSimulation(simulation);

    if (simulation.config?.systemInstruction?.parts?.[0]?.text) {
      useSettingsStore.getState().setPersona(simulation.config.systemInstruction.parts[0].text);
    }

    setView("meeting");
  }, []);

  // Settings mock func - settings dialog is global now
  const openSettings = () => {
    const event = new CustomEvent('open-settings');
    window.dispatchEvent(event);
  };


  const handleGenerateReport = useCallback(async () => {
    const { transcripts, startTime } = useTranscriptStore.getState().getTranscriptsSnapshot();

    if (transcripts.length === 0) {
      alert("Rapor oluşturmak için önce bir görüşme yapmalısınız.");
      return;
    }

    setLoading(true);
    setReportLoading(true);
    setView("report");

    try {
      const report = await analyzeConversation(transcripts, persona, startTime);
      setReport(report);

      // Save report to Supabase
      const supabaseClient = supabase;
      if (user && activeSimulation && supabaseClient) {
        try {
          const { error: saveError } = await supabaseClient
            .from('simulation_reports')
            .insert({
              user_id: user.id,
              simulation_id: activeSimulation.id,
              simulation_title: activeSimulation.title,
              report_data: report,
              score: report.overallScore
            });

          if (saveError) {
            console.error("Rapor kaydedilemedi:", saveError);
          } else {
            console.log("Rapor başarıyla kaydedildi.");
          }
        } catch (err) {
          console.error("Rapor kaydetme hatası:", err);
        }
      }

    } catch (error: any) {
      setReportError(error.message);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setReport, setReportLoading, setReportError, persona, user, activeSimulation]);

  const handleCloseReport = useCallback(() => {
    // Report store close logic moved or handled by just changing view
    setView("dashboard");
    useTranscriptStore.getState().clearTranscripts();
  }, []);

  const renderDashboard = () => {
    switch (user?.role) {
      case 'mudur':
        return <MudurDashboard onLogout={handleLogout} onStartSimulation={handleStartSimulation} />;
      case 'admin':
        return (
          <AdminDashboard
            onLogout={handleLogout}
            onViewReport={() => setView("report")}
          />
        );
      case 'personel':
      default:
        return (
          <PersonelDashboard
            onStartSimulation={handleStartSimulation}
            onOpenSettings={openSettings}
            onOpenProfile={() => setIsProfileOpen(true)}
            onLogout={handleLogout}
            onOpenPastSessions={() => setView("past-sessions")}
            showPastSessions={view === "past-sessions"}
            onOpenLeaderboard={() => setView("leaderboard")}
            showLeaderboard={view === "leaderboard"}
            onOpenModules={() => setView("modules")}
            showModules={view === "modules"}
            onStartModule={(mod) => {
              setActiveModule(mod);
              setView("module-player");
            }}
            onBack={() => setView("dashboard")}
            onViewReport={() => setView("report")}
          />
        );
    }
  };


  // Loading state (initial)
  if (!apiOptions) {
    // Allow login to render even if api key is fetching?
    // No, we need context. But auth check might be faster.
    // For now keep as is but check isLoading
    // Actually we have two loading states here, one for API key, one for auth/report gen variables
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  // Login sayfası before api key? No needs context possibly.
  if (view === "login" || !isAuthenticated) {
    return (
      <ErrorBoundary>
        <Login onLoginSuccess={handleLoginSuccess} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="App">
        <LiveAPIProvider options={apiOptions}>
          {view === "dashboard" || view === "past-sessions" || view === "leaderboard" || view === "modules" ? (
            renderDashboard()
          ) : view === "module-player" && activeModule ? (
            <ModulePlayer
              module={activeModule}
              onBack={() => {
                setActiveModule(null);
                setView("modules");
              }}
              onStartMainSimulation={(sim) => {
                handleStartSimulation(sim);
              }}
            />
          ) : view === "meeting" ? (
            <StreamingConsole
              onBack={() => setView("dashboard")}
              onGenerateReport={handleGenerateReport}
            />
          ) : view === "report" ? (
            <SalesReport onClose={handleCloseReport} />
          ) : (
            renderDashboard() // Fallback
          )}
          <SettingsDialog showButton={false} />
          {isProfileOpen && <ProfileSettings onClose={() => setIsProfileOpen(false)} />}
        </LiveAPIProvider>
      </div>
    </ErrorBoundary>
  );
}

export default App;
